/* eslint-disable react/require-default-props -- React deprecó defaultProps en componentes
   de función (avisa por consola en cada render): los valores por defecto van en la firma. */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { keyframes } from '@mui/material/styles';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import { formatDuration } from 'components/play/workoutEstimate';

const EASE = 'cubic-bezier(0.32,0.72,0,1)';
const ROW_HEIGHT = 52;

// Ecualizador de la pista que suena: tres barras que respiran. Solo `transform`, y se apaga
// con prefers-reduced-motion (queda la barra quieta, que ya identifica la fila).
const equalize = keyframes`
  0%, 100% { transform: scaleY(0.35); }
  50% { transform: scaleY(1); }
`;

function NowPlayingBars({ animated }) {
  return (
    <Stack direction="row" spacing={0.3} alignItems="flex-end" sx={{ height: 12 }} aria-hidden="true">
      {[0, 1, 2].map((bar) => (
        <Box
          key={bar}
          sx={{
            width: 2.5,
            height: 12,
            borderRadius: 999,
            backgroundColor: 'gold.main',
            transformOrigin: 'bottom',
            transform: 'scaleY(0.5)',
            ...(animated && {
              animation: `${equalize} 900ms ${EASE} infinite`,
              animationDelay: `${bar * 140}ms`
            }),
            '@media (prefers-reduced-motion: reduce)': { animation: 'none', transform: 'scaleY(0.7)' }
          }}
        />
      ))}
    </Stack>
  );
}

NowPlayingBars.propTypes = { animated: PropTypes.bool };

/**
 * OTRIX Play — cola de reproducción: la lista vertical de pistas del álbum, con la que suena
 * resaltada (superficie elevada + filete dorado a la izquierda) y su duración a la derecha.
 * Tocar una fila salta a esa pista.
 *
 * A partir de `maxVisible` filas la lista scrollea dentro de sí misma: una cola de 20 pistas
 * no puede empujar los controles del player fuera de la pantalla.
 *
 * En `readOnly` las filas dejan de ser botones (vista previa de lo que sonará): un botón que
 * no hace nada confunde al teclado y al lector de pantalla.
 */
function PlayTrackList({
  tracks,
  currentIndex,
  playing,
  onSelect,
  maxVisible = 6,
  ariaLabel,
  readOnly = false
}) {
  const { t } = useTranslation(['play']);

  if (!tracks || tracks.length === 0) return null;

  const scrolls = tracks.length > maxVisible;
  const interactive = !readOnly && Boolean(onSelect);

  return (
    <Box
      component="ul"
      aria-label={ariaLabel || t('music.queue', { ns: 'play', defaultValue: 'Lista de reproducción' })}
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
        ...(scrolls && {
          maxHeight: ROW_HEIGHT * maxVisible,
          overflowY: 'auto',
          // Scroll silencioso: barra fina que no compite con el contenido.
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'hairline.main', borderRadius: 999 }
        })
      }}>
      {tracks.map((track, index) => {
        const active = index === currentIndex;

        return (
          <Box component="li" key={track.id ?? index}>
            <Box
              component={interactive ? ButtonBase : 'div'}
              onClick={interactive ? () => onSelect(index) : undefined}
              aria-current={active ? 'true' : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                minHeight: ROW_HEIGHT,
                px: 1.25,
                py: 1,
                gap: 1.25,
                borderRadius: '10px',
                justifyContent: 'flex-start',
                textAlign: 'left',
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: active ? 'surface.raised' : 'transparent',
                transition: `background-color 200ms ${EASE}, transform 200ms ${EASE}`,
                ...(interactive && {
                  '&:hover': { backgroundColor: active ? 'surface.raised' : 'surface.overlay' },
                  '&:active': { transform: 'scale(0.98)' },
                  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'gold.main', outlineOffset: -2 },
                  // El índice se vuelve un play al pasar por encima, como en cualquier cola.
                  '&:hover .otx-track-index': { display: 'none' },
                  '&:hover .otx-track-play': { display: 'inline-flex' }
                }),
                // Filete dorado de la pista en curso (el acento, pequeño y a la izquierda).
                ...(active && {
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 8,
                    bottom: 8,
                    width: 3,
                    borderRadius: 999,
                    backgroundColor: 'gold.main'
                  }
                })
              }}>
              <Box
                sx={{
                  width: 24,
                  flex: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: active ? 'gold.main' : 'text.disabled'
                }}>
                {active ? (
                  <NowPlayingBars animated={playing} />
                ) : (
                  <>
                    <Typography
                      className="otx-track-index"
                      variant="caption"
                      sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                      {index + 1}
                    </Typography>
                    <PlayArrow className="otx-track-play" sx={{ fontSize: 16, display: 'none', color: 'text.primary' }} />
                  </>
                )}
              </Box>

              <Stack spacing={0.1} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={active ? 800 : 600} noWrap>
                  {track.title}
                </Typography>
                {track.category && (
                  <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
                    {t(`musicAdmin.categories.${track.category}`, { ns: 'play', defaultValue: track.category })}
                  </Typography>
                )}
              </Stack>

              <Typography
                variant="caption"
                sx={{
                  flex: 'none',
                  color: active ? 'text.secondary' : 'text.disabled',
                  fontVariantNumeric: 'tabular-nums'
                }}>
                {formatDuration(track.duration_seconds)}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

PlayTrackList.propTypes = {
  tracks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      category: PropTypes.string,
      duration_seconds: PropTypes.number
    })
  ),
  /** Índice de la pista en curso; -1 o null si no hay ninguna. */
  currentIndex: PropTypes.number,
  /** true = sonando (anima el ecualizador de la fila activa). */
  playing: PropTypes.bool,
  onSelect: PropTypes.func,
  /** Filas visibles antes de que la lista scrollee dentro de sí misma. */
  maxVisible: PropTypes.number,
  ariaLabel: PropTypes.string,
  /** true = vista previa: filas no interactivas (ni foco ni clic). */
  readOnly: PropTypes.bool
};

export default PlayTrackList;
